/**
 * @class SoulogHomeRecord
 * Home page recording timeline section (pure JS, no dependencies). Markup in sections/soulog-home-record.liquid.
 *
 * One phone per module at every width — the main phone stays on SENSE, the mirror phone (.srd__stage--mirror) shows Recap.
 *   Desktop (≥1024 landscape or ≥1367): sets data-desktop — the two modules side by side (title above its phone), about one screen tall;
 *   nothing pins and nothing switches on scroll (2026-09-28: the pin felt stuck; stacking and swap-column both felt empty).
 *   Texts fade up as they come into view (is-seen).
 *   Mobile / tablet: a single stacked column.
 * Common to both forms:
 *   · Clicking any [data-add] (left-side chip or phone bottom-bar button) → appends a matching entry to the end of the SENSE screen timeline,
 *     its time pushed later than the previous entry; the ruler gets a matching colored dot, and the timeline scrolls to the bottom to reveal the new row. Can keep accumulating.
 *   · The phone scales as a whole to fit the available space (--srd-s); the screen interior always lays out at the design mock's 402×874.
 *   · Recap screen: the App's summary page (assets/soulog-home-record-recap.js, a string of the whole exported HTML) is fetched
 *     only once the section comes within a screen of the viewport, then written into the displayed .srd__recap-frame (document.write).
 *     Not a Blob URL: the store sends CSP frame-ancestors 'none' + X-Frame-Options DENY, the blob document inherits it and Safari refuses
 *     to show it in a frame (blank phone, 2026-10-16). Not srcdoc: it resolves href="#xx" against the store URL and loads the shop in the phone.
 * Shopify compatibility: scoped to this element, initialized in connectedCallback, cleaned up in disconnectedCallback.
 */
if (!customElements.get("soulog-home-record")) {
	const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

	/** Pure functions, called directly by unit tests (tests/unit/soulog-home-record.test.mjs) */
	const math = {
		/** "05:32" / "1:02:03" → seconds; unparseable counts as 0 */
		parseTime(str) {
			const parts = String(str || "")
				.trim()
				.split(":")
				.map((x) => parseInt(x, 10));
			if (!parts.length || parts.some((x) => !Number.isFinite(x))) return 0;
			return parts.reduce((acc, x) => acc * 60 + x, 0);
		},
		/** Seconds → "05:32"; written as "1:02:03" once it reaches an hour */
		formatTime(sec) {
			const s = Math.max(0, Math.round(sec));
			const pad = (n) => String(n).padStart(2, "0");
			const h = Math.floor(s / 3600);
			const m = Math.floor((s % 3600) / 60);
			return (h ? h + ":" + pad(m) : pad(m)) + ":" + pad(s % 60);
		},
		/** Each append pushes the time 12~45 seconds later, looking like the rhythm of a real meeting */
		nextTime(prevSec, rand) {
			return prevSec + 12 + Math.floor(clamp(rand, 0, 0.9999) * 34);
		},
		/** Scale ratio for fitting the 450×920 phone frame into the available space */
		scaleFor(availW, availH) {
			return clamp(Math.min(availW / 450, availH / 920), 0.3, 1);
		},
		/** JSON from the theme editor textarea after newline_to_br | split → clean array of non-empty strings */
		cleanList(json) {
			let list;
			try {
				list = JSON.parse(json || "[]");
			} catch (e) {
				return [];
			}
			return Array.isArray(list)
				? list
						.map((x) =>
							String(x)
								.replace(/<br\s*\/?>/g, "")
								.trim(),
						)
						.filter(Boolean)
				: [];
		},
		/** Next chip to light after `cur`, skipping the clicked ones and wrapping around; -1 once every chip has been clicked */
		nextChip(used, cur) {
			const n = used.length;
			for (let k = 1; k <= n; k++) {
				const i = (((cur + k) % n) + n) % n;
				if (!used[i]) return i;
			}
			return -1;
		},
	};
	globalThis.SoulogHomeRecordMath = math;

	const DESKTOP_MQ = "(min-width: 1024px) and (orientation: landscape), (min-width: 1367px)";

	/** Ruler: 20px per tick, starting at -99px (aligned with the CSS); when a new colored dot passes 70% of the screen width, push the ruler left */
	const TICK = 20;
	const RULER_ORIGIN = -99;
	const RULER_FOLLOW_X = 280;
	/** Ruler length 1600px = 80 ticks; after pushing 50 ticks, wrap the whole thing back by 1000px (an integer multiple of the 100 tick period, so no visible jump) */
	const RULER_WRAP = 50;

	/** One chip lit per step (matches the 1.8s srd-chip-glow in the CSS) */
	const LIT_STEP = 1800;
	/**
	 * Recap "peek" (user request 2026-09-29: people don't realize the Recap phone scrolls): once the Recap phone has been on screen
	 * for PEEK_DELAY ms, the page inside it slides down PEEK_PX (its own px, before the phone scale) and back after PEEK_HOLD ms. Once only.
	 */
	const PEEK_DELAY = 1000;
	const PEEK_PX = 140;
	const PEEK_HOLD = 900;
	const bindMq = (mq, fn) => {
		if (mq.addEventListener) mq.addEventListener("change", fn);
		else if (mq.addListener) mq.addListener(fn);
	};
	const unbindMq = (mq, fn) => {
		if (mq.removeEventListener) mq.removeEventListener("change", fn);
		else if (mq.removeListener) mq.removeListener(fn);
	};

	/** Load the Recap summary page once per page (the section may be placed twice) → resolves to its HTML, or "" on failure */
	let recapHtml = null;
	const loadRecap = (src) => {
		if (recapHtml) return recapHtml;
		recapHtml = new Promise((resolve) => {
			const done = () => resolve(window.soulogRecapHtml || "");
			if (window.soulogRecapHtml || !src) return done();
			const tag = document.createElement("script");
			tag.src = src;
			tag.async = true;
			tag.onload = done;
			tag.onerror = done;
			document.head.appendChild(tag);
		});
		return recapHtml;
	};

	/**
	 * Lit-chip rotation (user request 2026-09-28): one chip glows at a time, in order, skipping clicked ones. Runs at every width.
	 * On desktop with a mouse, the shared demo cursor (assets/soulog-ghost-cursor.js) rests on the lit chip and follows it;
	 * the pointer entering either card (SENSE or Recap) merges it into a green system cursor.
	 */
	class ChipCursor {
		constructor(host) {
			this.host = host;
			this.chips = Array.from(host.querySelectorAll(".srd__chip"));
			this.lit = -1;
			this.reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");
			// Zone = both cards (2026-09-29 user: on the Recap card the cursor turns green too, the demo cursor flying over from its chip;
			// until then it was the SENSE card only). The cards are drawn by pseudo-elements, so take the union of the grid cells they
			// cover — text blocks and phones. One bounding box over both, gap included, so crossing between the cards doesn't flicker.
			const cells = [
				'.srd__item[data-index="0"]',
				".srd__stage:not(.srd__stage--mirror)",
				'.srd__item[data-index="1"]',
				".srd__stage--mirror",
			]
				.map((sel) => host.querySelector(sel))
				.filter(Boolean);
			const box = host.querySelector(".srd__chips");
			if (window.SoulogGhostCursor && box && cells.length) {
				this.ghost = new window.SoulogGhostCursor(host, {
					container: box,
					target: () => this.chips[this.lit] || null,
					zone: () =>
						cells.map((el) => el.getBoundingClientRect()).reduce((a, b) => window.SoulogGhostCursor.union(a, b)),
				});
			}
		}

		destroy() {
			this.stop();
			if (this.ghost) this.ghost.destroy();
		}

		/** Section entered / left the viewport: start / stop the rotation */
		setInview(on) {
			if (on) this.start();
			else this.stop();
		}

		start() {
			if (this.timer) return;
			if (this.lit < 0) this.step();
			if (this.reduceMq.matches) return;
			this.timer = setInterval(() => this.step(), LIT_STEP);
		}

		stop() {
			clearInterval(this.timer);
			this.timer = null;
		}

		/** Light the next unclicked chip (restarting the glow even when it's the same chip again) */
		step() {
			const used = this.chips.map((c) => c.hasAttribute("data-used"));
			const next = math.nextChip(used, this.lit);
			this.chips.forEach((c) => c.removeAttribute("data-lit"));
			this.lit = next;
			if (next >= 0) {
				void this.chips[next].offsetWidth;
				this.chips[next].setAttribute("data-lit", "");
			}
			if (this.ghost) this.ghost.park();
		}
	}

	class SoulogHomeRecord extends HTMLElement {
		connectedCallback() {
			this.items = Array.from(this.querySelectorAll(".srd__item"));
			this.tabs = this.items.map((it) => it.querySelector(".srd__tab"));
			this.stage = this.querySelector(".srd__stage");
			// Only count screens inside the main phone; the mirror phone for Recap on mobile (.srd__stage--mirror) is a static display and doesn't take part in switching
			this.panels = this.stage ? Array.from(this.stage.querySelectorAll(".srd__panel")) : [];
			this.N = this.items.length;
			this.feed = this.panels[0] && this.panels[0].querySelector(".srd__list");
			this.feedScroll = this.panels[0] && this.panels[0].querySelector(".srd__scroll");
			this.strip = this.stage && this.stage.querySelector(".srd__strip");
			this.head = this.querySelector(".srd__head");
			this.chipsEl = this.querySelector(".srd__chips");

			// Image URLs are space-separated rather than JSON: JSON escapes / as \/, which theme dev's asset proxy can't rewrite, so the preview would fetch the live theme's old files
			this.images = (this.dataset.images || "").split(/\s+/).filter(Boolean);
			this.notes = math.cleanList(this.dataset.notes);
			this.files = math.cleanList(this.dataset.files);
			this.counts = { image: 0, note: 0, file: 0 };

			const times = this.feed ? Array.from(this.feed.querySelectorAll(".srd__time")) : [];
			this.clock = times.length ? math.parseTime(times[times.length - 1].textContent) : 0;
			const marks = this.strip ? Array.from(this.strip.querySelectorAll(".srd__mark")) : [];
			this.markT = marks.reduce((t, el) => Math.max(t, parseFloat(el.style.getPropertyValue("--t")) || 0), 0);
			this.shift = 0;
			this.rulerBase = 0;

			this.index = 0;
			this.desktop = false;
			/** Last item the scroll position asked for; only a change of it switches (see update) */

			this.desktopMq = window.matchMedia(DESKTOP_MQ);
			this.reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");

			this.onResize = () => this.measure();
			this.onModeChange = () => {
				this.setup();
				this.fillRecap();
			};
			this.onClick = (e) => {
				const add = e.target.closest("[data-add]");
				if (add && this.contains(add)) {
					// Whichever kind was clicked turns off that chip's guide glow (same-kind buttons in the phone bottom bar count too); unclicked ones keep glowing
					const kind = add.dataset.add;
					this.querySelectorAll(`.srd__chip[data-add="${kind}"]`).forEach((chip) => chip.setAttribute("data-used", ""));
					this.add(kind);
					return;
				}
				const tab = e.target.closest(".srd__tab");
				if (tab) this.goTo(this.tabs.indexOf(tab));
			};
			this.onKeydown = (e) => {
				const k = this.tabs.indexOf(e.target);
				if (k < 0 || (e.key !== "ArrowDown" && e.key !== "ArrowUp")) return;
				e.preventDefault();
				const next = (k + (e.key === "ArrowDown" ? 1 : -1) + this.N) % this.N;
				this.tabs[next].focus();
				this.goTo(next);
			};

			this.addEventListener("click", this.onClick);
			this.addEventListener("keydown", this.onKeydown);
			bindMq(this.desktopMq, this.onModeChange);
			window.addEventListener("resize", this.onResize);

			// The button glow only runs while the section is on screen; stop it when scrolled away to save invisible repaints
			this.cursor = new ChipCursor(this);
			// Desktop texts fade up as they come into view and back out as they leave (CSS only acts on it under data-desktop)
			this.seenIo = new IntersectionObserver(
				(entries) => entries.forEach((en) => en.target.classList.toggle("is-seen", en.isIntersecting)),
				{ rootMargin: "0px 0px -10% 0px" },
			);
			this.querySelectorAll(".srd__item, .srd__copy").forEach((el) => this.seenIo.observe(el));

			this.viewIo = new IntersectionObserver((entries) => {
				entries.forEach((en) => {
					this.toggleAttribute("data-inview", en.isIntersecting);
					this.cursor.setInview(en.isIntersecting);
				});
			});
			if (this.chipsEl) this.viewIo.observe(this.chipsEl);

			// Recap page: start loading a screen ahead so it's ready by the time Recap comes up
			this.recapFrames = Array.from(this.querySelectorAll(".srd__recap-frame"));
			// Reset on every connect: the theme editor re-attaches the section and the new frames need filling again
			this.recapStarted = false;
			if (this.recapFrames.length) {
				this.recapIo = new IntersectionObserver(
					(entries) => {
						if (entries.some((en) => en.isIntersecting)) this.prepareRecap();
					},
					{ rootMargin: "100% 0px" },
				);
				this.recapIo.observe(this);
			}

			// Recap peek + scroll hint: watch the Recap phone (desktop only — on mobile it's an inert display copy)
			this.recapLoaded = false;
			this.recapVisible = false;
			this.peeked = false;
			const mirror = this.querySelector(".srd__stage--mirror");
			if (mirror) {
				this.peekIo = new IntersectionObserver(
					(entries) => {
						entries.forEach((en) => (this.recapVisible = en.isIntersecting));
						this.schedulePeek();
					},
					{ threshold: 0.6 },
				);
				this.peekIo.observe(mirror);
			}

			this.setIndex(0);
			this.setup();

			// The SENSE timeline opens at its TOP (it used to open scrolled to the bottom like the App, but then a wheel-down over
			// the phone had nowhere to go and scrolled the page instead — "the phone won't scroll", user report 2026-09-28).
			// Appending a row still scrolls to the bottom so the new row shows (see add()).
			// Once fonts load, line heights change: re-measure the phone scale
			if (document.fonts && document.fonts.ready) {
				document.fonts.ready.then(() => {
					if (!this.isConnected) return;
					this.measure();
				});
			}
		}

		disconnectedCallback() {
			if (this.cursor) this.cursor.destroy();
			if (this.viewIo) this.viewIo.disconnect();
			if (this.seenIo) this.seenIo.disconnect();
			if (this.recapIo) this.recapIo.disconnect();
			if (this.peekIo) this.peekIo.disconnect();
			clearTimeout(this.peekTimer);
			clearTimeout(this.peekBackTimer);
			this.removeEventListener("click", this.onClick);
			this.removeEventListener("keydown", this.onKeydown);
			unbindMq(this.desktopMq, this.onModeChange);
			window.removeEventListener("resize", this.onResize);
		}

		setup() {
			this.desktop = this.N > 1 && this.desktopMq.matches;
			this.toggleAttribute("data-desktop", this.desktop);
			// The Recap (mirror) phone is inert + aria-hidden in the markup — on mobile it's a display-only copy under the Recap text.
			// On desktop it IS the Recap phone next to the SENSE one: let people scroll the summary page inside it (user report 2026-09-28)
			const mirror = this.querySelector(".srd__stage--mirror");
			if (mirror) {
				mirror.inert = !this.desktop;
				if (this.desktop) mirror.removeAttribute("aria-hidden");
				else mirror.setAttribute("aria-hidden", "true");
			}
			this.setIndex(0);
			this.measure();
			this.greenFrame();
			this.schedulePeek();
		}

		/**
		 * Write the Recap page into the iframe: only the mirror phone's is ever shown (the main phone stays on SENSE).
		 * Written into the frame's own about:blank document, so no navigation and no frame-ancestors check; after document.open
		 * the frame's URL is this page's, so the summary's href="#xx" links stay fragment jumps inside the frame.
		 */
		fillRecap() {
			if (!this.recapHtml || !this.recapFrames) return;
			this.recapFrames.forEach((f) => {
				if (f.dataset.filled || !f.closest(".srd__stage--mirror")) return;
				const doc = f.contentDocument;
				if (!doc) return;
				f.dataset.filled = "1";
				doc.open();
				doc.write(this.recapHtml);
				doc.close();
				this.watchRecap(f);
			});
		}

		/**
		 * Phone scale ratio (shared by both phones). Desktop: the block (head + titles + phones + description) fits about one screen
		 * below the sticky top bar and above the floating buy pill (.sbp, 92px incl. its bottom gap), so the phones get ~62% of that height;
		 * mobile / tablet: each module gets its own phone, two stacked vertically, so make it smaller:
		 * width is 72% of the available width, at most 300px (about 250px wide on a 375 screen), and height no more than 65% of the viewport.
		 */
		measure() {
			if (!this.stage) return;
			const header = document.querySelector(".header.header-sticky--active");
			const headerH = header ? header.offsetHeight : 0;
			const vh = window.innerHeight;
			let availH;
			let availW = this.stage.clientWidth;
			if (this.desktop) {
				const pill = document.querySelector(".sbp") ? 92 : 0;
				availH = (vh - headerH - pill) * 0.62;
			} else {
				availW = Math.min(availW * 0.72, 300);
				// Take the viewport height only when the width changes (rotation). iOS toolbar show/hide changes innerHeight by 60~90px;
				// re-scaling the phones on that shifts everything below (Safari has no scroll anchoring), which reads as the page
				// scrolling back by itself, which toggles the toolbar again — a loop (user's device recording, 2026-09-28)
				if (this.mobileW !== window.innerWidth) {
					this.mobileW = window.innerWidth;
					this.mobileVh = vh;
				}
				availH = this.mobileVh * 0.65;
			}
			this.style.setProperty("--srd-s", math.scaleFor(availW, availH).toFixed(4));
		}

		/**
		 * The Recap page has loaded: any wheel / touch / click / key inside it means the visitor found the scroll on their own —
		 * no need to peek (the hint pill stays regardless) (mouse events over an iframe don't reach this page, so listen in the frame's document).
		 */
		watchRecap(f) {
			const doc = f.contentDocument;
			if (!doc) return;
			const used = () => this.markRecapUsed();
			["wheel", "touchstart", "pointerdown", "keydown"].forEach((t) =>
				doc.addEventListener(t, used, { passive: true, once: true }),
			);
			this.recapLoaded = true;
			this.greenFrame();
			this.schedulePeek();
		}

		/**
		 * The Recap phone sits inside the cursor zone, but it's an iframe: the page's green cursor rule doesn't reach it and pointer
		 * moves over it never reach this page. Give the frame the green cursor itself (desktop mouse only; styleFrame checks that).
		 * Also called from setup(), for a switch into desktop mode after the frame had loaded.
		 */
		greenFrame() {
			const g = this.cursor && this.cursor.ghost;
			const f = this.querySelector(".srd__stage--mirror .srd__recap-frame");
			if (g && f && this.recapLoaded) g.styleFrame(f);
		}

		markRecapUsed() {
			this.peeked = true;
			clearTimeout(this.peekTimer);
			// Mid-peek: leave the page where the visitor took it instead of yanking it back up
			clearTimeout(this.peekBackTimer);
		}

		/** Peek once the Recap phone has been on screen for PEEK_DELAY; leaving before that cancels it */
		schedulePeek() {
			clearTimeout(this.peekTimer);
			if (this.peeked || !this.recapLoaded || !this.recapVisible || !this.desktop || this.reduceMq.matches) return;
			this.peekTimer = setTimeout(() => this.peek(), PEEK_DELAY);
		}

		peek() {
			const el = this.recapDoc();
			if (!el || this.peeked) return;
			this.peeked = true;
			// Already scrolled (e.g. by a scrollbar drag the listeners missed): nothing to show
			if (el.scrollTop > 0) return;
			el.scrollTo({ top: PEEK_PX, behavior: "smooth" });
			this.peekBackTimer = setTimeout(() => {
				const e = this.recapDoc();
				if (e) e.scrollTo({ top: 0, behavior: "smooth" });
			}, PEEK_HOLD);
		}

		/** The mirror phone's Recap document (same origin, written in by fillRecap); null until then */
		recapDoc() {
			const f = this.querySelector(".srd__stage--mirror .srd__recap-frame");
			const doc = f && f.dataset.filled && f.contentDocument;
			return doc && doc.scrollingElement ? doc.scrollingElement : null;
		}

		setIndex(i) {
			if (i < 0 || i >= this.N) return;
			this.index = i;
			// CSS hides the recording chrome while data-index="1" (the Recap screen has its own top bar)
			this.dataset.index = String(i);
			this.items.forEach((it, k) => it.classList.toggle("is-on", k === i));
			this.tabs.forEach((tab, k) => {
				tab.setAttribute("aria-selected", k === i ? "true" : "false");
				tab.setAttribute("tabindex", k === i ? "0" : "-1");
			});
			this.panels.forEach((p, k) => {
				p.classList.toggle("is-on", k === i);
				p.setAttribute("aria-hidden", k === i ? "false" : "true");
			});
		}

		goTo(i) {
			if (i < 0 || i >= this.N) return;
			// Each item has its own phone (Recap uses the mirror phone); the main phone stays on SENSE
			this.setIndex(0);
		}

		/** Start loading the Recap page (once): on approach, or right away when something jumps here from far down the page */
		prepareRecap() {
			if (this.recapStarted || !this.recapIo) return;
			this.recapStarted = true;
			this.recapIo.disconnect();
			loadRecap(this.dataset.recapSrc).then((html) => {
				this.recapHtml = html;
				this.fillRecap();
			});
		}

		/**
		 * Where to scroll so item i is showing — for jumps from elsewhere on the page (the home CTA points, via
		 * assets/soulog-scroll-to-shop.js, which does the scrolling and re-measures after it lands).
		 * Every item has its own phone: land on whichever of the two starts higher (the title, both on desktop where it sits
		 * above its phone and on mobile), minus the sticky header.
		 */
		jumpTarget(i, headerOffset) {
			if (i < 0 || i >= this.N) return null;
			// The smooth scroll only reaches the approach margin at its very end — start the Recap load now so the phone isn't blank on arrival
			if (i === 1) {
				this.prepareRecap();
				// A jump means "show Recap from the top": drop whatever offset the user left inside the phone earlier
				const el = this.recapDoc();
				if (el) el.scrollTop = 0;
			}
			const phone = this.querySelector(i === 0 ? ".srd__stage:not(.srd__stage--mirror)" : ".srd__stage--mirror");
			let top = this.items[i].getBoundingClientRect().top;
			if (phone && phone.getClientRects().length) top = Math.min(top, phone.getBoundingClientRect().top);
			return top + window.scrollY - (headerOffset || 0);
		}

		/** Take the nth item (round-robin); return fallback when the list is empty */
		pick(list, kind, fallback) {
			if (!list.length) return fallback;
			const v = list[this.counts[kind] % list.length];
			this.counts[kind] += 1;
			return v;
		}

		add(kind) {
			const tpl = this.querySelector('template[data-tpl="' + kind + '"]');
			if (!tpl || !this.feed) return;
			// Appending only happens on the SENSE screen: if clicked while on Recap, switch back first so the user sees the new row
			if (this.index !== 0) this.setIndex(0);

			const row = tpl.content.firstElementChild.cloneNode(true);
			this.clock = math.nextTime(this.clock, Math.random());
			row.querySelector(".srd__time").textContent = math.formatTime(this.clock);

			if (kind === "image") {
				const img = row.querySelector(".srd__thumb");
				if (img) {
					img.loading = "eager";
					img.src = this.pick(this.images, "image", "");
				}
			} else if (kind === "note") {
				const note = row.querySelector(".srd__note");
				if (note) note.textContent = this.pick(this.notes, "note", "");
			} else if (kind === "file") {
				const [name, meta] = String(this.pick(this.files, "file", "Document.pdf")).split("|");
				const n = row.querySelector(".srd__file-name");
				const m = row.querySelector(".srd__file-meta");
				if (n) n.textContent = (name || "").trim();
				if (m) m.textContent = (meta || "").trim();
			}

			this.feed.appendChild(row);
			this.addMark(kind);

			const behavior = this.reduceMq.matches ? "auto" : "smooth";
			requestAnimationFrame(() => {
				this.feedScroll.scrollTo({ top: this.feedScroll.scrollHeight, behavior });
			});
		}

		/** Put a colored dot on the ruler; when it passes the follow line, push the ruler left */
		addMark(kind) {
			if (!this.strip) return;
			this.markT += 1;
			const mark = document.createElement("span");
			mark.className = "srd__mark srd__mark--" + kind + " is-new";
			mark.style.setProperty("--t", String(this.markT));
			this.strip.appendChild(mark);

			const x = this.markT * TICK + RULER_ORIGIN - this.shift * TICK;
			if (x > RULER_FOLLOW_X) this.shift += Math.ceil((x - RULER_FOLLOW_X) / TICK);
			this.strip.style.setProperty("--srd-shift", String(this.shift));

			if (this.shift >= RULER_WRAP && !this.wrapping) this.wrapRuler();
		}

		/**
		 * The ruler is only 80 ticks long. After pushing RULER_WRAP ticks, wait for this translation animation to finish,
		 * then disable the transition and shift the ruler, dots and labels back by RULER_WRAP ticks as a whole — the tick period (5 ticks) divides it evenly, so the picture doesn't jump.
		 */
		wrapRuler() {
			const strip = this.strip;
			this.wrapping = true;
			const run = () => {
				this.wrapping = false;
				strip.style.transition = "none";
				this.shift -= RULER_WRAP;
				this.markT -= RULER_WRAP;
				this.rulerBase += RULER_WRAP;
				strip.querySelectorAll(".srd__mark").forEach((el) => {
					const t = (parseFloat(el.style.getPropertyValue("--t")) || 0) - RULER_WRAP;
					if (t < 0) el.remove();
					else {
						el.style.setProperty("--t", String(t));
						el.classList.remove("is-new");
					}
				});
				strip.querySelectorAll(".srd__label").forEach((el) => {
					const k = parseFloat(el.style.getPropertyValue("--k")) || 0;
					el.textContent = math.formatTime(65 + 5 * k + this.rulerBase);
				});
				strip.style.setProperty("--srd-shift", String(this.shift));
				void strip.offsetWidth;
				strip.style.transition = "";
			};
			if (this.reduceMq.matches) run();
			else setTimeout(run, 520);
		}
	}

	customElements.define("soulog-home-record", SoulogHomeRecord);
}
